import { useState } from "react";
import { Link } from "react-router-dom";
import PlatformAuthShell from "./PlatformAuthShell";
import AuthField from "./AuthField";
import Button from "../../ui/Button";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { TRIAL_DAYS } from "../../../constants/trial";
import a from "./auth.module.css";

const STEP_LABELS = ["Клуб", "Поддомен", "Аккаунт", "Проверка"];

const EMPTY = {
  clinicName: "",
  subdomain: "",
  login: "",
  password: "",
  fullName: "",
  phone: "",
};

function subdomainPreview(sub) {
  const s = (sub || "club").toLowerCase().replace(/[^a-z0-9-]/g, "");
  return s || "club";
}

function subdomainHostLabel(sub) {
  try {
    return new URL(buildClinicPortalUrl(subdomainPreview(sub), "/")).host;
  } catch {
    return `${subdomainPreview(sub)}.localhost`;
  }
}

function phoneValid(phone) {
  return String(phone || "").replace(/\D/g, "").length >= 10;
}

export default function AuthClubRegisterFlow({
  loading,
  error,
  onSubmit,
  backTo = "/",
  backLabel = "Назад",
  onLogin,
}) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(EMPTY);
  const patch = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const validateStep = () => {
    switch (step) {
      case 1:
        return data.clinicName.trim().length >= 2;
      case 2:
        return /^[a-z0-9-]{2,32}$/.test(data.subdomain);
      case 3:
        return (
          /\S+@\S+\.\S+/.test(data.login) &&
          data.password.length >= 8 &&
          data.fullName.trim().length >= 2 &&
          phoneValid(data.phone)
        );
      default:
        return true;
    }
  };

  const next = (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    if (step < 4) setStep(step + 1);
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit(data);
  };

  const footerLink =
    step < 4 ? (
      <div className={a.footerLinks}>
        {onLogin ? (
          <span
            role="button"
            tabIndex={0}
            onClick={onLogin}
            onKeyDown={(e) => e.key === "Enter" && onLogin()}
          >
            Уже зарегистрированы? <span>Войти</span>
          </span>
        ) : (
          <Link to="/find">
            Уже зарегистрированы? <span>Найти и войти</span>
          </Link>
        )}
      </div>
    ) : null;

  return (
    <PlatformAuthShell
      backTo={backTo}
      backLabel={backLabel}
      title="Подключите клуб"
      subtitle={`${TRIAL_DAYS} дней демо-доступа — журнал, клиенты, финансы и кабинет спортсменов.`}
      stepCurrent={step}
      stepTotal={4}
      stepLabels={STEP_LABELS}
      footer={footerLink}
    >
      {error ? <div className={a.errorBox}>{error}</div> : null}

      {step === 1 && (
        <form onSubmit={next}>
          <AuthField
            label="Название клуба"
            value={data.clinicName}
            onChange={(e) => patch("clinicName", e.target.value)}
            placeholder="Секция дзюдо «Байтим»"
            required
          />
          <div className={a.actions}>
            <Button type="submit" block size="lg" disabled={!validateStep()}>
              Продолжить
            </Button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={next}>
          <AuthField
            label="Адрес клуба (поддомен)"
            value={data.subdomain}
            onChange={(e) =>
              patch(
                "subdomain",
                e.target.value.replace(/\s/g, "").toLowerCase(),
              )
            }
            placeholder="baytim"
            hint="Латиница, цифры и дефис — a-z, 0-9"
            required
          />
          <div className={a.previewBox}>
            <span className={a.previewLabel}>Ваш адрес</span>
            <span className={a.previewValue}>
              {subdomainHostLabel(data.subdomain)}
            </span>
          </div>
          <div className={a.actions}>
            <Button type="submit" block size="lg" disabled={!validateStep()}>
              Продолжить
            </Button>
            <Button type="button" variant="ghost" block onClick={() => setStep(1)}>
              Назад
            </Button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={next}>
          <AuthField
            label="Ваше имя"
            value={data.fullName}
            onChange={(e) => patch("fullName", e.target.value)}
            placeholder="Иванов Иван"
            autoComplete="name"
            required
          />
          <AuthField
            label="Телефон"
            type="tel"
            value={data.phone}
            onChange={(e) => patch("phone", e.target.value)}
            placeholder="+7 900 000-00-00"
            autoComplete="tel"
            hint="Обязательно — для связи и восстановления доступа"
            required
          />
          <AuthField
            label="Email"
            type="email"
            value={data.login}
            onChange={(e) => patch("login", e.target.value)}
            placeholder="you@club.ru"
            autoComplete="email"
            required
          />
          <AuthField
            label="Пароль"
            type="password"
            value={data.password}
            onChange={(e) => patch("password", e.target.value)}
            placeholder="Минимум 8 символов"
            autoComplete="new-password"
            minLength={8}
            required
          />
          <div className={a.actions}>
            <Button type="submit" block size="lg" disabled={!validateStep()}>
              Продолжить
            </Button>
            <Button type="button" variant="ghost" block onClick={() => setStep(2)}>
              Назад
            </Button>
          </div>
        </form>
      )}

      {step === 4 && (
        <form onSubmit={submit}>
          <div className={a.reviewBox}>
            <div className={a.reviewRow}>
              <span className={a.reviewLabel}>Клуб</span>
              <span className={a.reviewValue}>{data.clinicName}</span>
            </div>
            <div className={a.reviewRow}>
              <span className={a.reviewLabel}>Адрес</span>
              <span className={a.reviewValue}>
                {subdomainHostLabel(data.subdomain)}
              </span>
            </div>
            <div className={a.reviewRow}>
              <span className={a.reviewLabel}>Владелец</span>
              <span className={a.reviewValue}>{data.fullName}</span>
              <span className={a.reviewValueMuted}>{data.phone}</span>
              <span className={a.reviewValueMuted}>{data.login}</span>
            </div>
          </div>
          <div className={a.actions}>
            <Button type="submit" block size="lg" disabled={loading}>
              {loading ? "Создаём…" : "Создать клуб"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              block
              onClick={() => setStep(3)}
              disabled={loading}
            >
              Назад
            </Button>
          </div>
        </form>
      )}
    </PlatformAuthShell>
  );
}
